import { Form } from 'antd';
import { MultiStepModal, WizardStep } from 'components/MultiStepModal';
import { useAuthContext } from 'contexts/AuthContext';
import { useSubmitNouveauDossierConcessionnaire } from 'hooks/nouveauDossierConcessionnaire.hooks';
import useFormSubmittable from 'hooks/useFormSubmittable';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  ClientType,
  NouveauDossierConcessionnaireData,
  VehiculeType,
} from 'types/Concessionnaire';
import CloseDraftConfirmationModal from 'views/dossiers/components/CloseDraftConfirmationModal';
import MinimizedDraftEntryButton from 'views/dossiers/components/MinimizedDraftEntryButton';
import useConcessionnaireDraftSession from 'views/dossiers/concessionnaire/hooks/useConcessionnaireDraftSession';
import Step1 from 'views/dossiers/concessionnaire/steps/Step1';
import Step2 from 'views/dossiers/concessionnaire/steps/Step2';
import Step3 from 'views/dossiers/concessionnaire/steps/Step3';
import Success from 'views/dossiers/concessionnaire/steps/Success';
import { isPersonnePhysiqueVulCategory } from 'views/dossiers/concessionnaire/steps/step2/step2.selectors';
import ErrorPage from 'views/errors/Error.page';

interface NouveauDossierConcessionnaireProps {
  open: boolean;
  onClose: () => void;
  initialStep?: number;
  debugPreset?: string;
}

interface StepFrameProps {
  title: string;
  description: ReactNode;
  children: ReactNode;
}

function StepFrame({ title, description, children }: StepFrameProps) {
  return (
    <div className="mx-auto w-full max-w-[700px] [&_label]:!text-base">
      <div className="mb-6">
        <h2 className="mb-1 text-[20px] font-semibold leading-7 tracking-[0.25px] text-gris-hellio-6">
          {title}
        </h2>
        {description ? (
          <p className="text-sm leading-5 text-gris-clair-hellio">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function hasRequiredValue(value: unknown) {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  return value !== undefined && value !== null && value !== '';
}

function hasPersonneMoraleStep2Prerequisites(
  values: Partial<NouveauDossierConcessionnaireData>,
) {
  if (values.typeClientFinal !== ClientType.PERSONNE_MORALE) {
    return false;
  }

  const hasFabricationEuropeAnswer =
    values.vehiculeType !== VehiculeType.LEGER ||
    hasRequiredValue(values.produitEnEurope);

  return (
    hasRequiredValue(values.categorieVehicule) &&
    hasRequiredValue(values.typeAcheteur) &&
    hasRequiredValue(values.usageVehicules) &&
    hasRequiredValue(values.ficheDeLiaison?.id) &&
    hasFabricationEuropeAnswer
  );
}

function hasPersonnePhysiqueStep2Prerequisites(
  values: Partial<NouveauDossierConcessionnaireData>,
) {
  if (values.typeClientFinal !== ClientType.PERSONNE_PHYSIQUE) {
    return false;
  }

  const hasFabricationEuropeAnswer =
    !isPersonnePhysiqueVulCategory(values.categorieVehicule) ||
    hasRequiredValue(values.produitEnEurope);

  return (
    hasRequiredValue(values.categorieVehicule) &&
    hasRequiredValue(values.datePrevisionnelleDevis) &&
    hasRequiredValue(values.codePostal) &&
    hasRequiredValue(values.usageVehiculeParticulier) &&
    hasRequiredValue(values.statutPrecarite) &&
    hasFabricationEuropeAnswer
  );
}

function canAutoRunStep2(values?: Partial<NouveauDossierConcessionnaireData>) {
  if (!values) {
    return false;
  }

  if (values.typeClientFinal === ClientType.PERSONNE_MORALE) {
    return hasPersonneMoraleStep2Prerequisites(values);
  }

  if (values.typeClientFinal === ClientType.PERSONNE_PHYSIQUE) {
    return hasPersonnePhysiqueStep2Prerequisites(values);
  }

  return false;
}

export default function NouveauDossierConcessionnaire(
  props: NouveauDossierConcessionnaireProps,
) {
  const { open, onClose, initialStep = 1 } = props;
  const { t } = useTranslation(['nouveau_formulaire', 'global']);
  const [form] = Form.useForm<NouveauDossierConcessionnaireData>();
  const [state, setState] = useState<
    Partial<NouveauDossierConcessionnaireData>
  >({});
  const [isStep1Submittable, setIsStep1Submittable] = useState(true);
  const [isStep2Submittable, setIsStep2Submittable] = useState(true);
  const hasAutoCalculatedStep2OnRestoreRef = useRef(false);

  const { auth } = useAuthContext();
  const { contractRAI, id: userId } = auth?.user ?? {};

  const {
    submitStep1,
    submitStep2,
    buildFinalSubmitVariables,
    step2Mutation,
    step3Mutation,
    isPersonnePhysique,
  } = useSubmitNouveauDossierConcessionnaire({
    state,
    setState,
  });

  const session = useConcessionnaireDraftSession({
    open,
    initialStep,
    onClose,
    userId,
    form,
    isSubmitSuccess: step3Mutation.isSuccess,
    resetStep2Mutation: () => step2Mutation.reset?.(),
    resetStep3Mutation: () => step3Mutation.reset?.(),
  });
  const {
    setCurrentStepIndex,
    restoredAvisImpositionFiles,
    onDevisChange,
    onOtherFilesChange,
    onAvisImpositionChange,
  } = session;

  // Step2 runs its own scoped submittable hook.
  // Disable whole-form validation on step index 1 to avoid concurrent
  // validations on the same form instance (AntD outOfDate race).
  const isFormSubmittable = useFormSubmittable(form, {
    enabled: session.currentStepIndex !== 1,
  });
  const rai = state.ficheDeLiaison?.rai || contractRAI;
  const noRAI = !['3.1', '3.2'].includes(String(rai));

  const currentTypeClient = Form.useWatch('typeClientFinal', form);
  const hasNoFichesDeLiaison =
    currentTypeClient === ClientType.PERSONNE_MORALE &&
    (auth?.user?.fichesDeLiaison?.length ?? 0) === 0;
  const fichesDeLiaison = useMemo(
    () => auth?.user?.fichesDeLiaison ?? [],
    [auth?.user?.fichesDeLiaison],
  );

  useEffect(() => {
    if (currentTypeClient !== ClientType.PERSONNE_MORALE) {
      return;
    }

    if (fichesDeLiaison.length !== 1) {
      return;
    }

    if (!form.getFieldValue('ficheDeLiaison')) {
      form.setFieldValue('ficheDeLiaison', fichesDeLiaison[0]);
    }
  }, [currentTypeClient, fichesDeLiaison, form]);

  useEffect(() => {
    if (!session.isWizardOpen) {
      hasAutoCalculatedStep2OnRestoreRef.current = false;
    }
  }, [session.isWizardOpen]);

  useEffect(() => {
    if (!session.isHydrated || !session.isWizardOpen) {
      return;
    }

    if (session.currentStepIndex !== 2) {
      return;
    }

    if (step2Mutation.isLoading || step2Mutation.data) {
      return;
    }

    if (hasAutoCalculatedStep2OnRestoreRef.current) {
      return;
    }

    const restoredValues = form.getFieldsValue(
      true,
    ) as Partial<NouveauDossierConcessionnaireData>;

    if (!canAutoRunStep2(restoredValues)) {
      return;
    }

    hasAutoCalculatedStep2OnRestoreRef.current = true;
    submitStep2(restoredValues).catch(() => undefined);
  }, [
    form,
    session.currentStepIndex,
    session.isHydrated,
    session.isWizardOpen,
    step2Mutation.data,
    step2Mutation.isLoading,
    submitStep2,
  ]);

  const step2ErrorMessage =
    step2Mutation.error?.message ||
    (step2Mutation.error ? t('global:internet_connexion_error') : undefined);

  const handleWizardStepChange = useCallback(
    (nextStepIndex: number) => {
      if (nextStepIndex !== 1) {
        setIsStep2Submittable(true);
      }

      setCurrentStepIndex(nextStepIndex);
    },
    [setCurrentStepIndex],
  );

  const handleStep1Continue = useCallback(async () => {
    const values = await form.validateFields();
    submitStep1(values);
    return true;
  }, [form, submitStep1]);

  const handleStep2Continue = useCallback(async () => {
    const validatedValues = await form.validateFields();
    const allFormValues = form.getFieldsValue(true);

    return submitStep2({
      ...allFormValues,
      ...validatedValues,
    });
  }, [form, submitStep2]);

  const buildStep3Variables = useCallback(async () => {
    const values = await form.validateFields();
    return buildFinalSubmitVariables(values);
  }, [buildFinalSubmitVariables, form]);

  const stepTitles = useMemo(
    () => [
      t('nouveau_formulaire:etapes:etape_1'),
      t('nouveau_formulaire:etapes:etape_2'),
      t('nouveau_formulaire:etapes:etape_3'),
    ],
    [t],
  );

  const requiredFieldsText = t('global:champs_requis');
  const [requiredTextBefore, requiredTextAfter] = requiredFieldsText.split('*');

  const wizardSteps = useMemo<WizardStep[]>(
    () => [
      {
        id: 'step-1',
        title: stepTitles[0],
        next: {
          enabled:
            isFormSubmittable && !hasNoFichesDeLiaison && isStep1Submittable,
          onClick: handleStep1Continue,
        },
        content: (
          <StepFrame
            title={stepTitles[0]}
            description={
              <>
                {requiredTextBefore}
                <span className="text-rouge-copro-6">*</span>
                {requiredTextAfter}
              </>
            }
          >
            <Step1
              form={form}
              restoredAvisImpositionFiles={restoredAvisImpositionFiles}
              onAvisImpositionChange={onAvisImpositionChange}
              onSubmitAvailabilityChange={setIsStep1Submittable}
            />
          </StepFrame>
        ),
      },
      {
        id: 'step-2',
        title: stepTitles[1],
        next: {
          enabled: isStep2Submittable,
          loading: step2Mutation.isLoading,
          error: step2ErrorMessage,
          onClick: handleStep2Continue,
        },
        content: (
          <StepFrame
            title={stepTitles[1]}
            description={
              <>
                {requiredTextBefore}
                <span className="text-rouge-copro-6">*</span>
                {requiredTextAfter}
              </>
            }
          >
            <Step2
              form={form}
              rai={rai}
              onSubmitAvailabilityChange={setIsStep2Submittable}
              onDevisChange={onDevisChange}
              onOtherFilesChange={onOtherFilesChange}
              {...step2Mutation}
            />
          </StepFrame>
        ),
      },
      {
        id: 'step-3',
        title: stepTitles[2],
        next: {
          enabled: isFormSubmittable && !step2Mutation.isLoading,
        },
        content: (
          <StepFrame
            title={stepTitles[2]}
            description={
              currentTypeClient === ClientType.PERSONNE_PHYSIQUE
                ? t(
                    'nouveau_formulaire:resultat:montant_de_la_prime_description',
                  )
                : null
            }
          >
            <Step3
              form={form}
              rai={rai}
              goToPreviousStep={() => undefined}
              isLoadingPrime={step2Mutation.isLoading}
              showActions={false}
              showError={false}
              {...(step2Mutation.data ?? {})}
              {...step3Mutation}
            />
          </StepFrame>
        ),
      },
    ],
    [
      form,
      currentTypeClient,
      handleStep1Continue,
      handleStep2Continue,
      hasNoFichesDeLiaison,
      isFormSubmittable,
      isStep1Submittable,
      isStep2Submittable,
      rai,
      requiredTextAfter,
      requiredTextBefore,
      restoredAvisImpositionFiles,
      onAvisImpositionChange,
      onDevisChange,
      onOtherFilesChange,
      step2ErrorMessage,
      step2Mutation,
      step3Mutation,
      stepTitles,
      t,
    ],
  );

  if (!session.isHydrated) {
    return null;
  }

  if (noRAI && (session.isWizardOpen || session.isSuccessOpen)) {
    return <ErrorPage message={t('nouveau_formulaire:errors:no_RAI')} />;
  }

  return (
    <>
      <MultiStepModal
        open={session.isWizardOpen}
        steps={wizardSteps}
        currentStep={session.currentStepIndex}
        onStepChange={handleWizardStepChange}
        onRequestClose={session.requestClose}
        onRequestSuccessClose={session.closeAfterSuccess}
        onRequestMinimize={session.requestMinimize}
        isMinimizing={session.isMinimizing}
        destroyOnClose={false}
        wrapClassName="ajout-dossier-form-wrap"
        submit={{
          mutation: step3Mutation,
          getVariables: buildStep3Variables,
        }}
        success={({ closeSuccess }) => (
          <Success
            rai={rai}
            isPersonnePhysique={isPersonnePhysique}
            onCloseSuccess={closeSuccess}
          />
        )}
      />

      {session.isDraftActive && session.isDraftMinimized && (
        <MinimizedDraftEntryButton
          onClick={session.expandFromMinimized}
          labelKey="ajout_dossier:minimized_entry"
        />
      )}

      {session.isDraftActive && (
        <div
          ref={session.minimizeTargetRef}
          aria-hidden="true"
          className="fixed bottom-0 right-4 h-0 w-0"
        />
      )}

      <CloseDraftConfirmationModal
        open={session.isCloseConfirmationOpen}
        onCancel={session.cancelClose}
        onConfirm={session.confirmClose}
        translationPrefix="ajout_dossier:close_confirm"
      />
    </>
  );
}
